import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Dropdown } from 'react-bootstrap';

const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  displayKey,
  valueKey,
  searchKey,
  disabled = false,
  allowClear = true
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const searchText = option[searchKey] || '';
        return searchText.toLowerCase().includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchValue, options, searchKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleClear = () => {
    onChange(null);
    setSearchValue('');
  };

  const selectedOption = options.find(opt => opt[valueKey] === value);
  const displayText = selectedOption ? selectedOption[displayKey] : '';

  return (
    <div className="position-relative" ref={dropdownRef}>
      <div className="d-flex">
        <Form.Control
          type="text"
          value={isOpen ? searchValue : displayText}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-grow-1"
        />
        {allowClear && value && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm ms-1"
            onClick={handleClear}
            disabled={disabled}
            style={{ minWidth: '40px' }}
          >
            ×
          </button>
        )}
      </div>
      
      {isOpen && !disabled && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm"
          style={{ 
            top: '100%', 
            zIndex: 1060, 
            maxHeight: '150px', 
            overflowY: 'auto' 
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option[valueKey] || index}
                type="button"
                className="btn btn-link w-100 text-start text-decoration-none border-0 rounded-0"
                onClick={() => handleSelect(option)}
                style={{ 
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #dee2e6' : 'none',
                  color: '#212529',
                  padding: '6px 12px',
                  fontSize: '14px',
                  lineHeight: '1.2'
                }}
              >
                {option[displayKey]}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-muted" style={{ fontSize: '14px' }}>
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SearchableDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  displayKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  searchKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool
};

export default SearchableDropdown;
